import produce from 'immer';
import { keyBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  DEFAULT_CONDITIONAL,
  DEFAULT_DISPLAY_CONDITIONAL,
} from 'dashboard/lib/constants/case-template';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { CaseTemplate } from 'dashboard/models/case-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowDeploymentConfigurationLabel } from 'dashboard/models/workflow-deployment';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { ModuleButton } from 'dashboard/modules/Button';
import { ModuleCaseEmail } from 'dashboard/modules/CaseEmail';
import { clearCaseTemplate, setCaseTemplate } from 'dashboard/redux/actions/case-template';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListCaseVariableDefinitionsQuery } from 'dashboard/redux/api/case-variable-definition';
import { getExternalDataSources } from 'dashboard/redux/selectors/case-template';
import { getAllEnabledTemplates } from 'dashboard/redux/selectors/organization';
import { getPublishedVerificationTemplates } from 'dashboard/redux/selectors/verification-templates';
import { RootState } from 'dashboard/redux/store';
import { loadAccountStatuses } from 'dashboard/redux/thunk-actions/account-statuses';
import { loadAccountTypesFilterOptions } from 'dashboard/redux/thunk-actions/account-types';
import { loadCaseQueryList } from 'dashboard/redux/thunk-actions/case-queries';
import { createCaseQuery, updateCaseQuery } from 'dashboard/redux/thunk-actions/case-query';
import {
  loadCaseTemplate,
  updateCaseTemplateV2,
} from 'dashboard/redux/thunk-actions/case-template';
import { loadExternalIntegrations } from 'dashboard/redux/thunk-actions/external-integrations';
import { loadGraphsAndGraph } from 'dashboard/redux/thunk-actions/graph';
import { loadGraphQueryTemplates } from 'dashboard/redux/thunk-actions/graph-query-templates';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import { loadVerificationTemplatesList } from 'dashboard/redux/thunk-actions/verification-templates';
import { loadWorkflows } from 'dashboard/redux/thunk-actions/workflows';
import { DashboardCaseTemplatesShowView } from 'dashboard/views/DashboardCaseTemplatesShowView';
import { FormAddCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormAddCaseQuery';
import { FormEditCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormEditCaseQuery';
import { CaseTemplateSettingsFormData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/FormCaseTemplateSettings';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { caseTemplateId: string };

const DashboardCaseTemplatesShowRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(props.clearCaseTemplate);

  const { caseTemplateId } = useParams<Params>() as Params;

  const [caseTemplateIsDirty, setCaseTemplateIsDirty] = useState(false);
  const [caseTemplateIsValid, setCaseTemplateIsValid] = useState(true);

  const hasUserViewPermission = usePermission(Permission.UserView);
  const hasGraphQueryTemplateViewPermission = usePermission(Permission.GraphQueryTemplateView);
  const hasExternalIntegrationViewPermission = usePermission(Permission.ExternalIntegrationView);
  const hasVerificationTemplateViewPermission = usePermission(Permission.VerificationTemplateView);

  const hasCaseDataSourceQueryFeatureFlag = useFeatureFlag('dashboard:case-data-source-query');

  // -----------------------------------------------------------------------------------------
  // General loaders
  // -----------------------------------------------------------------------------------------

  useApiLoader({
    loader: () => props.loadAccountStatuses(),
    deps: [props.loadAccountStatuses],
    onError: () => props.showMessage('Failed to load account statuses.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadAccountTypesFilterOptions(),
    deps: [props.loadAccountTypesFilterOptions],
    onError: () => props.showMessage('Failed to load account types.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    skip: !hasUserViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadTags('all'),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // TODO: (melissa) split this into two calls so that we can correctly permission them
  const { loading: workflowLoading } = useApiLoader({
    loader: () =>
      props.loadWorkflows({
        triggerTypes: [ApiModel.TriggerCase],
      }),
    deps: [props.loadWorkflows],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load workflows.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading } = useApiLoader({
    loader: () => props.loadCaseTemplate(caseTemplateId),
    deps: [props.loadCaseTemplate, caseTemplateId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case template.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () =>
      props.loadVerificationTemplatesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: {},
      }),
    deps: [props.loadVerificationTemplatesList],
    skip: !hasVerificationTemplateViewPermission,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load verification templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Case template operations
  // -----------------------------------------------------------------------------------------

  const {
    loading: saveCaseTemplateV2Loading,
    error: saveCaseTemplateV2Error,
    callback: saveCaseTemplateV2,
  } = useApiCallback(async () => {
    if (!caseTemplateIsValid) {
      props.showMessage(
        'Cannot save invalid case template. Please fix and try again.',
        MessageType.Failure,
      );
    } else {
      const caseTemplate = props.caseTemplate;
      if (caseTemplate && caseTemplateIsDirty) {
        setCaseTemplateIsDirty(false);

        const firstCaseEmailFromAddress = (
          caseTemplate.attributes.configuration['case-layout-v2'].tabs
            .flatMap((tab) => tab.modules)
            .find((module) => module.type === ModuleTypes.CaseEmail) as ModuleCaseEmail
        )?.['default-from-email-address'];
        const caseActionWorkflowIds = caseTemplate.attributes.configuration[
          'case-layout-v2'
        ].tabs.reduce((actionIds, tab) => {
          tab.modules.forEach((module) => {
            if (module.type === ModuleTypes.Button) {
              const actionId = (module as ModuleButton)['action-id'];
              if (actionId) {
                actionIds.add(actionId);
              }
            }
          });
          return actionIds;
        }, new Set(caseTemplate.attributes.configuration['case-action-ids']));

        const nextCaseTemplate = produce(caseTemplate, (draftCaseTemplate) => {
          draftCaseTemplate.attributes.configuration['default-from-email-address'] =
            firstCaseEmailFromAddress ?? '';
          return draftCaseTemplate;
        });

        await props.updateCaseTemplateV2(
          nextCaseTemplate.id,
          nextCaseTemplate,
          Array.from(caseActionWorkflowIds),
        );
      }
      props.showMessage('Successfully updated case template.', MessageType.Success);
    }
  }, [props.updateCaseTemplateV2, props.caseTemplate, caseTemplateIsValid]);

  useApiErrorHandler({
    error: saveCaseTemplateV2Error,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const updateCaseTemplateV2 = (caseTemplate: CaseTemplate) => {
    setCaseTemplateIsDirty(true);
    props.setCaseTemplate(caseTemplate);
  };

  const { error: archiveCaseTemplateError, callback: archiveCaseTemplate } =
    useApiCallback(async () => {
      if (props.caseTemplate) {
        const { data: caseTemplateData } =
          await SessionClientDashboard.deleteResource<CaseTemplate>(
            ApiEndpoint.CaseTemplate,
            props.caseTemplate.id,
          );
        props.setCaseTemplate(caseTemplateData);
        props.showMessage('Successfully archived case template.', MessageType.Success);
      }
    }, [props.setCaseTemplate, props.caseTemplate]);

  useApiErrorHandler({
    error: archiveCaseTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to archive case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to archive case template, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: restoreCaseTemplateError, callback: restoreCaseTemplate } =
    useApiCallback(async () => {
      if (props.caseTemplate) {
        const { data: caseTemplateData } =
          await SessionClientDashboard.postResourceAction<CaseTemplate>(
            {
              type: ApiModel.CaseTemplate,
              id: props.caseTemplate.id,
            },
            'restore',
          );
        props.setCaseTemplate(caseTemplateData);
        props.showMessage('Successfully restored case template.', MessageType.Success);
      }
    }, [props.setCaseTemplate, props.caseTemplate]);

  useApiErrorHandler({
    error: restoreCaseTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to restore case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to restore case template, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Case query
  // -----------------------------------------------------------------------------------------
  const { loading: dataSourceLoading } = useApiLoader({
    loader: async () => {
      await props.loadExternalIntegrations();
    },
    deps: [props.loadExternalIntegrations],
    skip: !hasExternalIntegrationViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load external data sources.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: caseQueriesLoading, callback: loadCaseQueries } = useApiLoader({
    loader: () => props.loadCaseQueryList({ currentCaseTemplateId: caseTemplateId }),
    deps: [props.loadCaseQueryList, caseTemplateId],
    skip: !hasCaseDataSourceQueryFeatureFlag,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case queries.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: createCaseQueryError, callback: handleCreateCaseQuery } = useApiCallback(
    async (values: FormAddCaseQueryData) => {
      const { querySource } = values;

      if (querySource === 'javascript') {
        await props.createCaseQuery({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          caseTemplateId: props.caseTemplate.id,
          queryType: 'javascript',
          name: values.queryName,
          body: values.queryBody,
        });
      } else {
        await props.createCaseQuery({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          caseTemplateId: props.caseTemplate.id,
          queryType: 'datasource',
          dataSourceId: values.querySource.split('-')[1],
          name: values.queryName,
          body: values.queryBody,
        });
      }
      await loadCaseQueries();
      props.showMessage('Successfully added case query.', MessageType.Success);
    },
    [props.caseTemplate, props.createCaseQuery, loadCaseQueries],
  );

  useApiErrorHandler({
    error: createCaseQueryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create case query, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: updateCaseQueryError, callback: handleUpdateCaseQuery } = useApiCallback(
    async (values: FormEditCaseQueryData, id: string) => {
      if (values['querySource'] === 'javascript') {
        await props.updateCaseQuery({
          queryType: 'javascript',
          caseQueryId: id,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          caseTemplateId: props.caseTemplate.id,
          formData: values,
        });
      } else {
        await props.updateCaseQuery({
          queryType: 'datasource',
          caseQueryId: id,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          caseTemplateId: props.caseTemplate.id,
          formData: values,
        });
      }
      await loadCaseQueries();
      props.showMessage('Successfully updated case query.', MessageType.Success);
    },
    [props.caseTemplate, props.updateCaseQuery, loadCaseQueries],
  );

  useApiErrorHandler({
    error: updateCaseQueryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to update case query, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Query templates
  // -----------------------------------------------------------------------------------------
  const { loading: _isLoadingGraphQueryTemplates } = useApiLoader({
    loader: async () => {
      await props.loadGraphQueryTemplates();
    },
    deps: [],
    skip: !hasGraphQueryTemplateViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load graph query templates, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  // Fetch graph to get custom node datum type names
  const { loading: _loadingGraphsAndGraph } = useApiLoader({
    loader: () => {
      return props.loadGraphsAndGraph(props.currentEnvironment.id);
    },
    deps: [props.currentEnvironment],
    skip: !hasGraphQueryTemplateViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const {
    loading: saveCaseTemplateSettingsLoading,
    error: saveCaseTemplateSettingsError,
    callback: saveCaseTemplateSettings,
  } = useApiCallback(
    async ({
      'case-template-name': caseTemplateName,
      'case-template-prefix': caseTemplatePrefix,
      'assignment-policies': assignmentPolicies,
      'triage-configuration-unassign-cases-when-unavailable': unassignCasesWhenUnavailable,
      'triage-configuration-policy-tokens-created-cases':
        triageConfigurationPolicyTokensCreatedCases,
      'triage-configuration-policy-tokens-unassigned-cases':
        triageConfigurationPolicyTokensUnassignedCases,
      'case-creation-email-addresses': caseCreationEmailAddresses,
      resolutions,
      states,
      'state-tags': stateTags,
      actions,
      'field-schema': fieldSchema,
      deactivateFieldSchemaKeys,
    }: CaseTemplateSettingsFormData) => {
      if (props.caseTemplate == null) {
        props.showMessage('Failed to update case template. Please try again.', MessageType.Failure);
        return;
      }

      const checklistItems = props.caseTemplate.attributes.configuration['checklist-items'];
      const activeStateIds = (states || [])
        .filter((s) => !s['archived-at'])
        .map((state) => state.token);

      const correctedChecklistItems = checklistItems.map((item) => {
        const correctedRequiredFor = item['required-for'].filter((item) => {
          if (item.startsWith('state_')) {
            // use custom states as checklist item
            return activeStateIds.includes(item);
          } else {
            // use resolution as checklist item
            return resolutions.includes(item);
          }
        });
        return {
          ...item,
          'required-for': correctedRequiredFor,
        };
      });

      const correctedAssignmentPolicies = assignmentPolicies.map((policy) => {
        // if the conditional is DEFAULT_DISPLAY_CONDITIONAL, convert it
        // into DEFAULT_CONDITIONAL so that JSONLogic can evaluate it
        const correctedConditional =
          JSON.stringify(policy.conditional) === JSON.stringify(DEFAULT_DISPLAY_CONDITIONAL)
            ? DEFAULT_CONDITIONAL
            : policy.conditional;

        return {
          ...policy,
          conditional: correctedConditional,
        };
      });

      const newCaseTemplate = {
        ...props.caseTemplate,
        attributes: {
          ...props.caseTemplate.attributes,
          name: caseTemplateName,
          'case-prefix': caseTemplatePrefix,
          configuration: {
            ...props.caseTemplate.attributes.configuration,
            'assignment-policies': correctedAssignmentPolicies,
            'triage-configuration': {
              ...props.caseTemplate.attributes.configuration['triage-configuration'],
              'unassign-cases-when-unavailable': unassignCasesWhenUnavailable,
              'policy-tokens-created-cases': triageConfigurationPolicyTokensCreatedCases,
              'policy-tokens-unassigned-cases': triageConfigurationPolicyTokensUnassignedCases,
            },
            resolutions: resolutions,
            subscriptions: {
              ...props.caseTemplate.attributes.configuration.subscriptions,
              'case-creation-email-addresses': caseCreationEmailAddresses,
            },
            'checklist-items': correctedChecklistItems,
            states: states,
            'state-tags': stateTags,
            'field-schema': fieldSchema,
            // N.B: in migration until we allow editing case header from layout editor
            // currently case-action-ids controls which case actions show in the case header.
            'case-action-ids': actions,
          },
        },
      };

      const caseActionWorkflowIds = props.caseTemplate.attributes.configuration[
        'case-layout-v2'
      ].tabs.reduce((actionIds, tab) => {
        tab.modules.forEach((module) => {
          if (module.type === ModuleTypes.Button) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            actionIds.add((module as ModuleButton)['action-id']);
          }
        });
        return actionIds;
      }, new Set(actions));

      await props.updateCaseTemplateV2(
        props.caseTemplate.id,
        newCaseTemplate,
        Array.from(caseActionWorkflowIds),
        deactivateFieldSchemaKeys,
      );
      props.showMessage('Successfully updated case template.', MessageType.Success);
    },
    [props.updateCaseTemplateV2, props.caseTemplate],
  );

  useApiErrorHandler({
    error: saveCaseTemplateSettingsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    error: variableDefinitionsListQueryError,
    data: caseVariableDefinitions = [],
    isLoading: caseVariableDefinitionsLoading,
    isFetching: caseVariableDefinitionsFetching,
  } = useListCaseVariableDefinitionsQuery(caseTemplateId);

  useRTKErrorHandler({
    error: variableDefinitionsListQueryError,
    onError: () => ({ message: 'Failed to load case variable definitions' }),
  });

  const workflowDeploymentMap = keyBy('id', props.workflowDeployments);
  const triggersMap = keyBy('id', props.triggers);

  const workflowsForCurrentOrganization = useMemo(() => {
    if (workflowLoading) return [];
    return (
      props.workflows
        .map((workflow) => [
          workflow,
          workflowDeploymentMap[workflow.relationships['active-deployment'].data?.id]?.attributes[
            'configuration'
          ]?.versions?.find(
            (version) => version.label === WorkflowDeploymentConfigurationLabel.Treatment,
          )?.token ?? workflow.relationships['latest-published-version'].data?.id,
        ])
        .map(([workflow, versionId]) => [
          workflow,
          props.workflowVersions.find((wfv) => wfv.id === versionId),
        ])
        .filter(([_workflow, workflowVersion]) => workflowVersion != null)
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .map(([workflow, workflowVersion]: [Workflow, WorkflowVersion]) => {
          return {
            workflow,
            workflowVersion,
            workflowDeployment:
              workflowDeploymentMap[workflow?.relationships?.['active-deployment']?.data?.id],
            trigger: triggersMap[workflowVersion.relationships.trigger.data.id],
          };
        })
    );
  }, [props.workflows, props.workflowVersions, workflowLoading]);

  const caseActionWorkflows = useMemo(() => {
    if (workflowLoading) return [];
    return workflowsForCurrentOrganization.map((wf) => wf.workflow);
  }, [workflowsForCurrentOrganization, workflowLoading]);

  const hasCasesBasic = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesBasic,
    props.currentEnvironment.type,
  );
  const hasCasesEnhanced = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesEnhanced,
    props.currentEnvironment.type,
  );
  const hasCasesEnterprise = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesEnterprise,
    props.currentEnvironment.type,
  );

  const isLoading =
    loading ||
    saveCaseTemplateV2Loading ||
    saveCaseTemplateSettingsLoading ||
    workflowLoading ||
    caseQueriesLoading ||
    dataSourceLoading ||
    caseVariableDefinitionsLoading;
  if (props.caseTemplate == null || props.caseDataManagerSchema == null || isLoading) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message='We couldn’t find that Case Template.'
          backRoute='/dashboard/case-templates'
        />
      </Backdrop>
    );
  }

  return (
    <>
      <NavConfirmPrompt prevent={caseTemplateIsDirty} />
      <DashboardCaseTemplatesShowView
        organization={props.organization}
        currentEnvironment={props.currentEnvironment}
        caseTemplate={props.caseTemplate}
        caseDataManagerSchema={props.caseDataManagerSchema}
        caseVariableDefinitions={caseVariableDefinitions}
        accountStatuses={props.accountStatuses}
        tags={props.tags}
        users={props.users.filter((user) => user.attributes.status === 'active')}
        currentUser={props.currentUser}
        actionWorkflows={caseActionWorkflows}
        caseQueries={props.caseQueries}
        dataSources={props.dataSources || []}
        isLayoutDirty={caseTemplateIsDirty}
        isLoadingCaseVariableDefinitions={
          caseVariableDefinitionsLoading || caseVariableDefinitionsFetching
        }
        hasCasesBasic={hasCasesBasic}
        hasCasesEnhanced={hasCasesEnhanced}
        hasCasesEnterprise={hasCasesEnterprise}
        updateCaseTemplateV2={updateCaseTemplateV2}
        saveCaseTemplateV2={saveCaseTemplateV2}
        saveCaseTemplateSettings={saveCaseTemplateSettings}
        setCaseTemplateIsValid={setCaseTemplateIsValid}
        enabledInquiryTemplates={props.enabledTemplates}
        publishedVerificationTemplates={props.publishedVerificationTemplates}
        accountTypeFilterOptions={props.accountTypeFilterOptions}
        graphQueryTemplates={props.graphQueryTemplates || []}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        graph={props.graph}
        handleCreateCaseQuery={handleCreateCaseQuery}
        handleUpdateCaseQuery={handleUpdateCaseQuery}
        archiveCaseTemplate={archiveCaseTemplate}
        restoreCaseTemplate={restoreCaseTemplate}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountStatuses: state.accountStatuses.accountStatuses,
    accountTypeFilterOptions: state.accountTypes.filterOptions,
    caseQueries: state.caseQueries.caseQueries,
    tags: state.tags.tags.all,
    users: state.user.users,
    currentUser: state.currentUser.currentUser,
    caseTemplate: state.caseTemplate.caseTemplate,
    caseVariableDefinitions: state.caseTemplate.caseVariableDefinitions,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    enabledTemplates: getAllEnabledTemplates(state),
    publishedVerificationTemplates: getPublishedVerificationTemplates(state),
    dataSources: getExternalDataSources(state),
    workflows: state.workflows.workflows,
    workflowVersions: state.workflows.workflowVersions,
    workflowDeployments: state.workflows.workflowDeployments,
    triggers: state.workflows.triggers,
    organization: state.organization.organization,
    caseDataManagerSchema: state.caseTemplate.caseDataManagerSchema,
    graphQueryTemplates: state.graphQueryTemplates.graphQueryTemplates,
    graph: state.graph.graph,
  };
};

const mapDispatchToProps = {
  loadAccountStatuses,
  loadAccountTypesFilterOptions,
  loadExternalIntegrations,
  loadCaseQueryList,
  loadUserList,
  loadTags,
  loadCaseTemplate,
  loadWorkflows,
  loadGraphsAndGraph,
  loadGraphQueryTemplates,
  loadVerificationTemplatesList,
  updateCaseTemplateV2,
  setCaseTemplate,
  createCaseQuery,
  updateCaseQuery,
  clearCaseTemplate,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseTemplatesShowRoute);
